import { urlFormat } from "../../seo/seo-helper";
import {
  ListingCandidate,
  CreateListingConfig,
  Rules,
  UpdateListingsResult,
} from "../../types/coveo-types";
import { PropertyDocument } from "../../types/property-types";
import { getListings, createListing } from "../../data/coveo/listings-api";

export const updateListings = async (
  properties: PropertyDocument[],
): Promise<UpdateListingsResult> => {
  // Get all the current Coveo listings
  const listings = await getListings({ perPage: 100 });

  // Get all the listing candidates
  const listingCandidates = properties
    .filter((property) => property.city && property.state)
    .map((property) => buildListingCandidate(property));

  // Get all the distinct candidates
  const distinctCandidates = Array.from(
    new Map(
      listingCandidates.map((candidate) => [
        buildListingName(candidate),
        candidate,
      ]),
    ).values(),
  );

  // Get all the candidates that are not in the current listings
  const newCandidates = distinctCandidates.filter(
    (candidate) =>
      !listings.some((listing) => listing.name === buildListingName(candidate)),
  );

  // Create the body required to create each new listing
  const createListingConfigs = newCandidates.map((candidate) =>
    buildCreateListingBody(candidate),
  );

  const results: UpdateListingsResult = {
    listingsToCreate: newCandidates,
    listingsToCreateTotal: newCandidates.length,
    listingIdsCreated: [],
    listingIdsCreatedTotal: 0,
    failedConfigs: [],
  };

  // Create each new listing
  for (const config of createListingConfigs) {
    const result = await createListing(config);

    if ("id" in result) {
      results.listingIdsCreated.push(result.id);
      results.listingIdsCreatedTotal += 1;
    } else {
      results.failedConfigs.push(result);
    }
  }

  return results;
};

const buildListingCandidate = (
  property: PropertyDocument,
): ListingCandidate => {
  return {
    city: property.city,
    state: property.state,
    countryCode: property.countryCode,
  };
};

const buildListingName = (candidate: ListingCandidate): string => {
  return `${candidate.city}, ${candidate.state}, ${candidate.countryCode}`
    .toLowerCase()
    .replaceAll("-", " ");
};

export const buildListingUrl = (candidate: ListingCandidate): string => {
  return `/s/${urlFormat(candidate.city, false)}-${urlFormat(candidate.state, false)}`;
};

const mapCountry = (_countryCode: string): string => {
  // At the moment, we only support US as this is hard coded in [[...path]].tsx in the search app
  return "US";
};

const mapCurrency = (_countryCode: string): string => {
  // At the moment, we only support USD as this is hard coded in [[...path]].tsx in the search app
  return "USD";
};

const mapLanguage = (_countryCode: string): string => {
  // At the moment, we only support en as this is hard coded in [[...path]].tsx in the search app
  return "en";
};

const buildListingDescription = (candidate: ListingCandidate): string => {
  return `Community in ${candidate.city}, ${candidate.state}`;
};

const buildRules = (candidate: ListingCandidate): Rules => {
  return {
    filterRules: [
      {
        action: "include",
        essential: true,
        filters: [
          {
            fieldName: "city",
            operator: "isExactly",
            value: {
              type: "string",
              value: candidate.city,
            },
          },
          {
            fieldName: "state",
            operator: "isExactly",
            value: {
              type: "string",
              value: candidate.state,
            },
          },
        ],
        locales: [
          {
            country: mapCountry(candidate.countryCode),
            currency: mapCurrency(candidate.countryCode),
            language: mapLanguage(candidate.countryCode),
          },
        ],
        name: buildListingDescription(candidate),
      },
    ],
  };
};

const buildCreateListingBody = (
  candidate: ListingCandidate,
): CreateListingConfig => {
  const hosts = process.env.CoveoListingsHosts
    ? process.env.CoveoListingsHosts.split(",")
    : [];

  const patterns = hosts.map((host) => {
    return {
      url: `${host}${buildListingUrl(candidate)}`,
    };
  });

  return {
    name: buildListingName(candidate),
    patterns: patterns,
    rules: buildRules(candidate),
    scope: "specific",
    trackingId: process.env.NEXT_PUBLIC_CoveoTrackingId as string,
  };
};
